import React, { useState, useEffect } from 'react';
import { Modal } from '@vadiun/react-components';
import moment from 'moment';
import {
  TravelEstimationResult,
  TravelEstimationsWithPrice
} from 'app/viaje/services/distanceAndPriceViajeTrasladoCalculator';
import { faCalculator, faChartBar } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface Props {
  distanceAndCost: TravelEstimationResult[];
  isOpen: boolean;
  handleClose: () => void;
}

const ReservaTrasladoEstimationDialog = ({
  distanceAndCost,
  isOpen,
  handleClose
}: Props) => {
  const [shouldAnimate, setShouldAnimate] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShouldAnimate(true);
    } else {
      setShouldAnimate(false);
    }
  }, [isOpen]);

  const handleCloseModal = () => {
    setShouldAnimate(false);
    handleClose();
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleCloseModal}
      body={
        <div className="relative">
          <div className="text-center mb-6 mt-10">
            <div className="flex items-center justify-center gap-3 mb-2 relative">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center"
                style={{
                  boxShadow: '-5px -5px 15px rgba(105, 14, 233, 0.4)',
                  position: 'absolute',
                  top: '-70px',
                  right: '260px'
                }}
              >
                <FontAwesomeIcon
                  icon={faCalculator}
                  className="text-white text-4xl"
                  style={{
                    color: '#690EE9'
                  }}
                />
              </div>
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center"
                style={{
                  boxShadow: '5px 5px 10px rgba(204, 14, 233, 0.4)',
                  position: 'absolute',
                  top: '-55px',
                  right: '220px'
                }}
              >
                <FontAwesomeIcon
                  icon={faChartBar}
                  className="text-white text-2xl"
                  style={{
                    color: '#CC0EE9'
                  }}
                />
              </div>
            </div>
            <h2 className="text-2xl font-bold  mt-2" style={{
              color: '#260054'
            }}>
              Estimación de Costos y Distancias
            </h2>
            <p className="text-gray-600 mt-1">Resumen detallado por viaje</p>
          </div>

          {distanceAndCost.length > 0 && (
            <table className="text-center w-full mt-8">
              <thead className="border-b-2">
                <tr>
                  <th>Viaje #</th>
                  <th>Distancia</th>
                  <th>Duración</th>
                  <th>Costo</th>
                </tr>
              </thead>
              <tbody>
                {distanceAndCost.map((result, index) => {
                  // Si es servicio grupal, mostrar mensaje especial
                  if ('isServiceGrupal' in result && result.isServiceGrupal) {
                    return (
                      <tr
                        key={index}
                        className="bg-gray-50"
                        style={{
                          animation: shouldAnimate
                            ? `slideUp 0.6s ease-out ${index * 150}ms both`
                            : 'none',
                          opacity: shouldAnimate ? 0 : 1,
                          transform: shouldAnimate
                            ? 'translateY(60px)'
                            : 'translateY(0)'
                        }}
                      >
                        <td className="p-2">{result.viajeIndex}</td>
                        <td className="p-2" colSpan={3}>
                          <span className="text-blue-600 font-medium">
                            {result.message}
                          </span>
                        </td>
                      </tr>
                    );
                  }

                  // Si es estimación normal, mostrar datos
                  const normalResult = result as TravelEstimationsWithPrice;
                  return (
                    <tr
                      key={index}
                      style={{
                        animation: shouldAnimate
                          ? `slideUp 0.6s ease-out ${index * 150}ms both`
                          : 'none',
                        opacity: shouldAnimate ? 0 : 1,
                        transform: shouldAnimate
                          ? 'translateY(50px)'
                          : 'translateY(0)'
                      }}
                    >
                      <td className="p-2">{index + 1}</td>
                      <td className="p-2">{normalResult.distance} km</td>
                      <td className="p-2">
                        {moment('2020-01-01 00:00:00')
                          .minutes(Math.ceil(normalResult.durationTraffic))
                          .format('HH:mm')}{' '}
                        hs.
                      </td>
                      <td className="p-2">$ {normalResult.price}</td>
                    </tr>
                  );
                })}

                {/* Fila de total */}
                {(() => {
                  const totalPrice = distanceAndCost
                    .filter(
                      (result) =>
                        !('isServiceGrupal' in result && result.isServiceGrupal)
                    )
                    .reduce((sum, result) => {
                      const normalResult = result as TravelEstimationsWithPrice;
                      return sum + normalResult.price;
                    }, 0);

                  return (
                    <tr
                      className="border-t-2 border-gray-300 bg-gray-100 font-semibold"
                      style={{
                        animation: shouldAnimate
                          ? `slideUp 0.6s ease-out ${
                              distanceAndCost.length * 150
                            }ms both`
                          : 'none',
                        opacity: shouldAnimate ? 0 : 1,
                        transform: shouldAnimate
                          ? 'translateY(60px)'
                          : 'translateY(0)'
                      }}
                    >
                      <td className="p-2"></td>
                      <td className="p-2"></td>
                      <td className="text-right">Costo Total:</td>
                      <td className="p-2">$ {totalPrice.toFixed(2)}</td>
                    </tr>
                  );
                })()}
              </tbody>
            </table>
          )}

          <style
            dangerouslySetInnerHTML={{
              __html: `
              @keyframes slideUp {
                from {
                  opacity: 0;
                  transform: translateY(30px);
                }
                to {
                  opacity: 1;
                  transform: translateY(0);
                }
              }
            `
            }}
          />
          <p className="text-center text-sm text-gray-500 mt-2" style={{
            position: 'absolute',
            bottom: '-29px',
            left: '0px',
          }}>
            * El Servicio Grupal no es tenido en cuenta para este cálculo de costos.
          </p>
        </div>
      }
    />
  );
};

export default ReservaTrasladoEstimationDialog;
